import { events, pageViewTypes } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getShowStudyPackViews } from "@helpers/exams";
import useAnalytics from "@hooks/useAnalytics";
import { LanguageLearningProgressApplet } from "@memorang/ui/components/LanguageLearningProgressApplet";
import { MicrophonePermissionDialog } from "@memorang/ui/components/MicroPhonePermissionDialog";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdaptiveSessionStart } from "../hooks/useAdaptiveSessionStart";
import { useLanguageLearningProgress } from "../hooks/useLanguageLearningProgress";
import ItemTypeDetailModalContainer from "./ItemTypeDetailModalContainer";
import SkillDetailDialogContainer from "./SkillDetailDialogContainer";

const LanguageLearningProgressContainer = () => {
	const memoizedAnalyticsParams = useMemo(
		() => ({ type: pageViewTypes.progress }),
		[],
	);
	useAnalytics(memoizedAnalyticsParams);

	const { t } = useTranslation();
	const { showSessionConfig } = useSessionConfigDialog();
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const [detailSkillId, setDetailSkillId] = useState<string>();
	const [detailQuestionTypeId, setDetailQuestionTypeId] = useState<string>();
	const {
		startAdaptiveSession,
		isCreatingAdaptiveSession,
		showMicPermissionDialog,
		setShowMicPermissionDialog,
	} = useAdaptiveSessionStart();

	const {
		isLoading,
		title,
		subtitle,
		stats,
		sections,
		skeletonProps,
		showEmptyState,
		tip,
		tipLoading,
	} = useLanguageLearningProgress();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const statsWithPress = stats.map((stat) => ({
		...stat,
		onPress: stat.skillId
			? () => {
					trackCustomEvent({
						eventName: events.skillTilePressed,
						surface: "progress",
						skill: stat.label,
						tileState: stat.cta ? "cta" : "known",
					});
					setDetailSkillId(stat.skillId);
				}
			: undefined,
	}));

	return (
		<>
			<LanguageLearningProgressApplet
				isLoading={isLoading}
				skeletonProps={skeletonProps}
				title={title}
				subtitle={subtitle}
				stats={statsWithPress}
				sections={sections}
				onViewAllItemTypes={(sectionId) =>
					router.push(`/progress/item-types/${sectionId}`)
				}
				onViewItemTypeDetails={setDetailQuestionTypeId}
				onStartTest={(sectionId) => setDetailSkillId(sectionId)}
				tip={tip}
				tipLoading={tipLoading}
				showEmptyState={showEmptyState}
				emptyState={{
					title: t("emptyState.progress.title"),
					subtitle: t("emptyState.progress.subtitle"),
					cta: t("emptyState.progress.cta"),
					onAction: () => {
						if (showStudyPackViews) {
							showSessionConfig({});
						} else {
							router.push("/home");
						}
					},
				}}
			/>
			<SkillDetailDialogContainer
				skillId={detailSkillId}
				onClose={() => setDetailSkillId(undefined)}
				onStartPractice={(task) => startAdaptiveSession(task, "skill_detail")}
				startingPractice={isCreatingAdaptiveSession}
			/>
			<ItemTypeDetailModalContainer
				questionTypeId={detailQuestionTypeId}
				onClose={() => setDetailQuestionTypeId(undefined)}
				onStartRecommendedSession={startAdaptiveSession}
				starting={isCreatingAdaptiveSession}
			/>
			<MicrophonePermissionDialog
				showDialog={showMicPermissionDialog}
				setShowDialog={() => setShowMicPermissionDialog(false)}
			/>
		</>
	);
};

export default LanguageLearningProgressContainer;
